import { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { AppContext } from '../../../contexts/AppContext';
import api from '../../../services/api';
import {
  Grid,
  Button,
  Paper,
  TextField,
  MenuItem,
  Select,
  Text,
  Divider,
  Chip,
  Checkbox,
  DataPicker,
  TimePicker
} from '../../core';
import { FormControlLabel } from '@material-ui/core';
import {
  SeccionHeader,
  ConfirmDialog,
  AlertDialog,
  UserSelect
} from '../../patterns';

const MESSAGE_TYPES = [
  { value: 'content', label: 'Conteúdo' },
  { value: 'reengagement', label: 'Reengajamento' },
  { value: 'operational', label: 'Operacional' }
];

const AUDIENCE_TYPES = [
  { value: 'all', label: 'Todos os usuários' },
  { value: 'user', label: 'Usuário específico' },
  { value: 'users', label: 'Seleção múltipla' },
  { value: 'group', label: 'Grupo salvo' },
  { value: 'segment', label: 'Segmento dinâmico' }
];

const LANGUAGE_OPTIONS = [
  { value: '', label: 'Qualquer' },
  { value: 'pt', label: 'Português' },
  { value: 'en', label: 'Inglês' },
  { value: 'es', label: 'Espanhol' }
];

const DESTINATION_SCREENS = [
  { value: 'LessonDetails', label: 'Detalhe da Lição' },
  { value: 'LessonsList', label: 'Lista de Lições' },
  { value: 'CyclesList', label: 'Lista de Ciclos' },
  { value: 'MainTabNavigator', label: 'Aba do App' }
];

const MAIN_TABS = [
  { value: 'Dashboard', label: 'Dashboard' },
  { value: 'CycleList', label: 'Ciclos' },
  { value: 'SearchList', label: 'Busca' },
  { value: 'SupportManTab', label: 'Apoio' },
  { value: 'UserProfile', label: 'Perfil' }
];

const emptyNotification = () => ({
  title: { pt: '', en: '', es: '' },
  body: { pt: '', en: '', es: '' },
  messageType: 'content',
  destination: { screen: 'CyclesList', lessonId: '', cycleId: '', tab: 'Dashboard' },
  audience: {
    type: 'all',
    userId: null,
    userIds: [],
    groupId: null,
    segment: {
      language: '',
      speaker: false,
      inactiveDays: '',
      country: '',
      state: '',
      city: '',
      hasCompletedLessons: false
    }
  },
  status: 'draft',
  scheduledAt: null
});

export default function NotificationEdit() {
  const { setLoading } = useContext(AppContext);
  const { id } = useParams();
  const history = useHistory();
  const pollingRef = useRef(null);

  const [notification, setNotification] = useState(emptyNotification());
  const [cycles, setCycles] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [userGroups, setUserGroups] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [previewCount, setPreviewCount] = useState(null);
  const [sendProgress, setSendProgress] = useState(null);
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');

  const [state, setState] = useState({
    title: id === 'new' ? 'Nova Notificação' : 'Editar Notificação',
    showConfirmDialog: false,
    showScheduleConfirmDialog: false,
    showUserSelect: false,
    userSelectMulti: false,
    errors: {},
    audienceBlocked: false,
    audienceBlockMessage: ''
  });

  const [alert, setAlert] = useState({
    show: false,
    errors: [{ message: 'Ocorreu um erro' }]
  });

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  useEffect(() => () => stopPolling(), [stopPolling]);

  useEffect(() => {
    api.get('/admin/cycles?sort=number:asc')
      .then((res) => setCycles(res.data.cycles || []))
      .catch(() => setCycles([]));
  }, []);

  useEffect(() => {
    api.get('/admin/user-groups?page=1&per_page=100&sort=name:asc')
      .then((res) => setUserGroups(res.data.usergroups || []))
      .catch(() => setUserGroups([]));
  }, []);

  useEffect(() => {
    const cycleId = notification.destination?.cycleId;
    if (!cycleId || !['LessonDetails', 'LessonsList'].includes(notification.destination?.screen)) {
      setLessons([]);
      return;
    }

    api.get(`/admin/lessons?page=1&per_page=100&sort=number:asc&q=cycleId:eq:${cycleId}`)
      .then((res) => setLessons(res.data.lessons || []))
      .catch(() => setLessons([]));
  }, [notification.destination?.cycleId, notification.destination?.screen]);

  useEffect(() => {
    if (id !== 'new') {
      setLoading(true);
      api
        .get(`/admin/push-notifications/${id}`)
        .then((res) => {
          const data = res.data.pushnotification;
          setNotification({
            ...emptyNotification(),
            ...data,
            destination: { ...emptyNotification().destination, ...data.destination },
            audience: {
              ...emptyNotification().audience,
              ...data.audience,
              segment: {
                ...emptyNotification().audience.segment,
                ...(data.audience?.segment || {})
              }
            }
          });
          if (data.scheduledAt) {
            const scheduled = new Date(data.scheduledAt);
            setScheduledDate(scheduled.toISOString().slice(0, 10));
            setScheduledTime(scheduled.toTimeString().slice(0, 5));
          }
          setState((prev) => ({
            ...prev,
            title: data.title?.pt || 'Editar Notificação'
          }));
          setLoading(false);
        })
        .catch((err) => {
          setAlert({
            errors: err.response?.data?.errors || [{ message: 'Erro ao carregar notificação' }],
            show: true
          });
          setLoading(false);
          history.push('/app/notifications');
        });
    }
  }, [id, setLoading, history]);

  const handleI18nChange = (field, lang, value) => {
    setNotification((prev) => ({
      ...prev,
      [field]: { ...prev[field], [lang]: value }
    }));
    setState((prev) => {
      const newErrors = { ...prev.errors };
      delete newErrors[`${field}.${lang}`];
      return { ...prev, errors: newErrors };
    });
  };

  const handleChange = ({ target }) => {
    const { name, value } = target;
    setNotification((prev) => ({ ...prev, [name]: value }));
  };

  const handleDestinationChange = ({ target }) => {
    const { name, value } = target;
    setNotification((prev) => ({
      ...prev,
      destination: { ...prev.destination, [name]: value }
    }));
  };

  const handleAudienceTypeChange = ({ target }) => {
    const type = target.value;
    setNotification((prev) => ({
      ...prev,
      audience: {
        ...emptyNotification().audience,
        type
      }
    }));
    setSelectedUser(null);
    setSelectedUsers([]);
    setPreviewCount(null);
    setState((prev) => ({ ...prev, audienceBlocked: false, audienceBlockMessage: '' }));
  };

  const handleGroupChange = ({ target }) => {
    setNotification((prev) => ({
      ...prev,
      audience: { ...prev.audience, groupId: target.value || null }
    }));
    setPreviewCount(null);
  };

  const handleSegmentChange = ({ target }) => {
    const { name, value, type, checked } = target;
    const segmentValue = type === 'checkbox' ? checked : value;
    setNotification((prev) => ({
      ...prev,
      audience: {
        ...prev.audience,
        segment: { ...prev.audience.segment, [name]: segmentValue }
      }
    }));
    setPreviewCount(null);
  };

  const handleUserSelected = (selection) => {
    if (state.userSelectMulti) {
      setSelectedUsers(selection);
      setNotification((prev) => ({
        ...prev,
        audience: {
          ...prev.audience,
          type: 'users',
          userIds: selection.map((u) => u.id || u._id)
        }
      }));
    } else {
      setSelectedUser(selection);
      setNotification((prev) => ({
        ...prev,
        audience: {
          ...prev.audience,
          type: 'user',
          userId: selection?.id || selection?._id || null
        }
      }));
    }
    setState((prev) => ({ ...prev, showUserSelect: false }));
    setPreviewCount(null);
  };

  const buildPayload = ({ status = 'draft', scheduledAt = null } = {}) => {
    const destination = { screen: notification.destination.screen };
    if (notification.destination.screen === 'LessonDetails') {
      destination.lessonId = notification.destination.lessonId;
      destination.cycleId = notification.destination.cycleId;
    } else if (notification.destination.screen === 'LessonsList') {
      destination.cycleId = notification.destination.cycleId;
    } else if (notification.destination.screen === 'MainTabNavigator') {
      destination.tab = notification.destination.tab;
    }

    const audience = { type: notification.audience.type };
    if (notification.audience.type === 'user') {
      audience.userId = notification.audience.userId;
    } else if (notification.audience.type === 'users') {
      audience.userIds = notification.audience.userIds;
    } else if (notification.audience.type === 'group') {
      audience.groupId = notification.audience.groupId;
    } else if (notification.audience.type === 'segment') {
      const seg = notification.audience.segment || {};
      const segment = {};
      if (seg.language) segment.language = seg.language;
      if (seg.speaker) segment.speaker = true;
      if (seg.inactiveDays !== '' && seg.inactiveDays != null) {
        segment.inactiveDays = Number(seg.inactiveDays);
      }
      if (seg.country?.trim()) segment.country = seg.country.trim();
      if (seg.state?.trim()) segment.state = seg.state.trim();
      if (seg.city?.trim()) segment.city = seg.city.trim();
      if (seg.hasCompletedLessons) segment.hasCompletedLessons = true;
      audience.segment = segment;
    }

    const payload = {
      pushnotification: {
        title: {
          pt: notification.title.pt?.trim(),
          en: notification.title.en?.trim() || undefined,
          es: notification.title.es?.trim() || undefined
        },
        body: {
          pt: notification.body.pt?.trim(),
          en: notification.body.en?.trim() || undefined,
          es: notification.body.es?.trim() || undefined
        },
        messageType: notification.messageType,
        destination,
        audience,
        status
      }
    };

    if (scheduledAt) {
      payload.pushnotification.scheduledAt = scheduledAt;
    }

    return payload;
  };

  const validateFields = () => {
    const errors = {};

    if (!notification.title.pt?.trim()) {
      errors['title.pt'] = 'Título em português é obrigatório';
    }
    if (!notification.body.pt?.trim()) {
      errors['body.pt'] = 'Corpo em português é obrigatório';
    }

    const { screen, lessonId, cycleId, tab } = notification.destination;
    if (screen === 'LessonDetails') {
      if (!cycleId) errors.cycleId = 'Ciclo é obrigatório';
      if (!lessonId) errors.lessonId = 'Lição é obrigatória';
    }
    if (screen === 'LessonsList' && !cycleId) {
      errors.cycleId = 'Ciclo é obrigatório';
    }
    if (screen === 'MainTabNavigator' && !tab) {
      errors.tab = 'Aba é obrigatória';
    }

    if (notification.audience.type === 'user' && !notification.audience.userId) {
      errors.audience = 'Selecione um usuário';
    }
    if (notification.audience.type === 'users' && (!notification.audience.userIds || notification.audience.userIds.length === 0)) {
      errors.audience = 'Selecione pelo menos um usuário';
    }
    if (notification.audience.type === 'group' && !notification.audience.groupId) {
      errors.audience = 'Selecione um grupo salvo';
    }

    return errors;
  };

  const validateScheduleFields = () => {
    const errors = validateFields();
    if (!scheduledDate) {
      errors.scheduledDate = 'Data de agendamento é obrigatória';
    }
    if (!scheduledTime) {
      errors.scheduledTime = 'Horário de agendamento é obrigatório';
    }
    if (scheduledDate && scheduledTime) {
      const scheduledAt = new Date(`${scheduledDate}T${scheduledTime}`);
      if (scheduledAt <= new Date()) {
        errors.scheduledAt = 'Data/hora deve ser no futuro';
      }
    }
    return errors;
  };

  const getScheduledAtIso = () => {
    if (!scheduledDate || !scheduledTime) return null;
    return new Date(`${scheduledDate}T${scheduledTime}`).toISOString();
  };

  const saveNotification = async ({ status = 'draft', scheduledAt = null } = {}) => {
    const payload = buildPayload({ status, scheduledAt });
    if (notification.id) {
      const res = await api.patch(`/admin/push-notifications/${notification.id}`, payload);
      return res.data.pushnotification;
    }
    const res = await api.post('/admin/push-notifications', payload);
    return res.data.pushnotification;
  };

  const fetchPreviewAudience = async (notificationId) => {
    const res = await api.get(`/admin/push-notifications/${notificationId}/preview-audience`);
    return res.data;
  };

  const startStatusPolling = (notificationId) => {
    stopPolling();
    pollingRef.current = setInterval(() => {
      api
        .get(`/admin/push-notifications/${notificationId}/status`)
        .then((res) => {
          const { status, sendProgress: progress } = res.data;
          if (progress) {
            setSendProgress(progress);
          }
          if (status !== 'sending') {
            stopPolling();
            setLoading(false);
            history.push('/app/notifications');
          }
        })
        .catch(() => {
          stopPolling();
          setLoading(false);
        });
    }, 2000);
  };

  const handleSaveDraft = async () => {
    const validationErrors = validateFields();
    if (Object.keys(validationErrors).length > 0) {
      setState((prev) => ({ ...prev, errors: validationErrors }));
      setAlert({
        show: true,
        errors: [{ message: 'Por favor, preencha todos os campos obrigatórios' }]
      });
      return;
    }

    setLoading(true);
    try {
      await saveNotification();
      setLoading(false);
      history.push('/app/notifications');
    } catch (err) {
      setLoading(false);
      setAlert({
        errors: err.response?.data?.errors || [{ message: 'Erro ao salvar rascunho' }],
        show: true
      });
    }
  };

  const handleSendNowClick = async () => {
    const validationErrors = validateFields();
    if (Object.keys(validationErrors).length > 0) {
      setState((prev) => ({ ...prev, errors: validationErrors }));
      setAlert({
        show: true,
        errors: [{ message: 'Por favor, preencha todos os campos obrigatórios' }]
      });
      return;
    }

    setLoading(true);
    try {
      const saved = await saveNotification();
      setNotification((prev) => ({ ...prev, id: saved.id }));

      const preview = await fetchPreviewAudience(saved.id);
      setPreviewCount(preview.eligibleUsers);

      if (!preview.eligibleUsers || preview.eligibleUsers === 0) {
        setLoading(false);
        setState((prev) => ({
          ...prev,
          audienceBlocked: true,
          audienceBlockMessage: 'Nenhum destinatário elegível encontrado. Verifique a audiência selecionada — os usuários precisam ter tokens de push registrados.'
        }));
        return;
      }

      setLoading(false);
      setState((prev) => ({ ...prev, showConfirmDialog: true }));
    } catch (err) {
      setLoading(false);
      setAlert({
        errors: err.response?.data?.errors || [{ message: 'Erro ao preparar envio' }],
        show: true
      });
    }
  };

  const handleConfirmSend = async () => {
    setState((prev) => ({ ...prev, showConfirmDialog: false }));
    setLoading(true);

    try {
      const notificationId = notification.id;
      await api.post(`/admin/push-notifications/${notificationId}/send`);
      setSendProgress({ processed: 0, total: previewCount || 0 });
      startStatusPolling(notificationId);
    } catch (err) {
      setLoading(false);
      setAlert({
        errors: err.response?.data?.errors || [{ message: 'Erro ao enviar notificação' }],
        show: true
      });
    }
  };

  const handleScheduleClick = async () => {
    const validationErrors = validateScheduleFields();
    if (Object.keys(validationErrors).length > 0) {
      setState((prev) => ({ ...prev, errors: validationErrors }));
      setAlert({
        show: true,
        errors: [{ message: 'Por favor, preencha todos os campos obrigatórios para agendamento' }]
      });
      return;
    }

    setLoading(true);
    try {
      const saved = await saveNotification({ status: 'draft' });
      setNotification((prev) => ({ ...prev, id: saved.id }));

      const preview = await fetchPreviewAudience(saved.id);
      setPreviewCount(preview.eligibleUsers);

      if (!preview.eligibleUsers || preview.eligibleUsers === 0) {
        setLoading(false);
        setState((prev) => ({
          ...prev,
          audienceBlocked: true,
          audienceBlockMessage: 'Nenhum destinatário elegível encontrado. Verifique a audiência selecionada — os usuários precisam ter tokens de push registrados.'
        }));
        return;
      }

      setLoading(false);
      setState((prev) => ({ ...prev, showScheduleConfirmDialog: true }));
    } catch (err) {
      setLoading(false);
      setAlert({
        errors: err.response?.data?.errors || [{ message: 'Erro ao preparar agendamento' }],
        show: true
      });
    }
  };

  const handleConfirmSchedule = async () => {
    setState((prev) => ({ ...prev, showScheduleConfirmDialog: false }));
    setLoading(true);

    try {
      const scheduledAt = getScheduledAtIso();
      await saveNotification({ status: 'scheduled', scheduledAt });
      setLoading(false);
      history.push('/app/notifications');
    } catch (err) {
      setLoading(false);
      setAlert({
        errors: err.response?.data?.errors || [{ message: 'Erro ao agendar notificação' }],
        show: true
      });
    }
  };

  const handleCancel = () => {
    if (notification.id) {
      history.push(`/app/notifications/${notification.id}`);
    } else {
      history.push('/app/notifications');
    }
  };

  const getConfirmQuestion = () => {
    const audienceLabel = AUDIENCE_TYPES.find((a) => a.value === notification.audience.type)?.label || '';
    return `Confirma o envio imediato desta notificação para ~${previewCount} usuário(s)?\n\nTítulo: ${notification.title.pt}\nAudiência: ${audienceLabel}`;
  };

  const getScheduleConfirmQuestion = () => {
    const audienceLabel = AUDIENCE_TYPES.find((a) => a.value === notification.audience.type)?.label || '';
    const scheduledLabel = scheduledDate && scheduledTime
      ? new Date(`${scheduledDate}T${scheduledTime}`).toLocaleString('pt-BR')
      : '—';
    return `Confirma o agendamento desta notificação para ~${previewCount} usuário(s)?\n\nTítulo: ${notification.title.pt}\nAudiência: ${audienceLabel}\nAgendado para: ${scheduledLabel}`;
  };

  return (
    <>
      <Grid container>
        <SeccionHeader title={state.title} />
        <AlertDialog
          open={alert.show}
          handleClose={() => setAlert({ ...alert, show: false })}
          errors={alert.errors}
        />

        {sendProgress && (
          <Grid item xs={12}>
            <Paper style={{ padding: 16, marginBottom: 16, backgroundColor: '#e3f2fd' }}>
              <Text style={{ fontWeight: 700 }}>Enviando notificação...</Text>
              <Text>
                Progresso: {sendProgress.processed}/{sendProgress.total}
              </Text>
            </Paper>
          </Grid>
        )}

        {state.audienceBlocked && (
          <Grid item xs={12}>
            <Paper style={{ padding: 16, marginBottom: 16, backgroundColor: '#fff3e0' }}>
              <Text style={{ color: '#e65100' }}>{state.audienceBlockMessage}</Text>
            </Paper>
          </Grid>
        )}

        <Grid item xs={12}>
          <Paper style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: 20 }}>
            <Text variant="h6">Conteúdo</Text>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Título (PT) *"
                  variant="outlined"
                  value={notification.title.pt || ''}
                  fullWidth
                  error={state.errors.hasOwnProperty('title.pt')}
                  helperText={state.errors['title.pt']}
                  onChange={(e) => handleI18nChange('title', 'pt', e.target.value)}
                  style={{ marginBottom: 16 }}
                />
                <TextField
                  label="Título (EN)"
                  variant="outlined"
                  value={notification.title.en || ''}
                  fullWidth
                  onChange={(e) => handleI18nChange('title', 'en', e.target.value)}
                  style={{ marginBottom: 16 }}
                />
                <TextField
                  label="Título (ES)"
                  variant="outlined"
                  value={notification.title.es || ''}
                  fullWidth
                  onChange={(e) => handleI18nChange('title', 'es', e.target.value)}
                  style={{ marginBottom: 16 }}
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <TextField
                  label="Corpo (PT) *"
                  variant="outlined"
                  value={notification.body.pt || ''}
                  fullWidth
                  multiline
                  rows={3}
                  error={state.errors.hasOwnProperty('body.pt')}
                  helperText={state.errors['body.pt']}
                  onChange={(e) => handleI18nChange('body', 'pt', e.target.value)}
                  style={{ marginBottom: 16 }}
                />
                <TextField
                  label="Corpo (EN)"
                  variant="outlined"
                  value={notification.body.en || ''}
                  fullWidth
                  multiline
                  rows={2}
                  onChange={(e) => handleI18nChange('body', 'en', e.target.value)}
                  style={{ marginBottom: 16 }}
                />
                <TextField
                  label="Corpo (ES)"
                  variant="outlined"
                  value={notification.body.es || ''}
                  fullWidth
                  multiline
                  rows={2}
                  onChange={(e) => handleI18nChange('body', 'es', e.target.value)}
                  style={{ marginBottom: 16 }}
                />
              </Grid>
            </Grid>

            <Divider style={{ margin: '16px 0' }} />

            <Text variant="h6">Configuração</Text>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Select
                  label="Tipo de mensagem"
                  id="messageType"
                  name="messageType"
                  fullWidth
                  handleChange={handleChange}
                  value={notification.messageType}
                >
                  {MESSAGE_TYPES.map((t) => (
                    <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                  ))}
                </Select>
              </Grid>
            </Grid>

            <Divider style={{ margin: '16px 0' }} />

            <Text variant="h6">Destino (Deep Link)</Text>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Select
                  label="Tela de destino"
                  id="screen"
                  name="screen"
                  fullWidth
                  handleChange={handleDestinationChange}
                  value={notification.destination.screen}
                >
                  {DESTINATION_SCREENS.map((s) => (
                    <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                  ))}
                </Select>
              </Grid>

              {(notification.destination.screen === 'LessonDetails' ||
                notification.destination.screen === 'LessonsList') && (
                <Grid item xs={12} md={4}>
                  <Select
                    label="Ciclo *"
                    id="cycleId"
                    name="cycleId"
                    fullWidth
                    handleChange={handleDestinationChange}
                    value={notification.destination.cycleId || ''}
                  >
                    <MenuItem value="">Selecione...</MenuItem>
                    {cycles.map((cycle) => (
                      <MenuItem key={cycle.id} value={cycle.id}>
                        {cycle.number} - {cycle.title}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
              )}

              {notification.destination.screen === 'LessonDetails' && (
                <Grid item xs={12} md={4}>
                  <Select
                    label="Lição *"
                    id="lessonId"
                    name="lessonId"
                    fullWidth
                    handleChange={handleDestinationChange}
                    value={notification.destination.lessonId || ''}
                  >
                    <MenuItem value="">Selecione...</MenuItem>
                    {lessons.map((lesson) => (
                      <MenuItem key={lesson.id} value={lesson.id}>
                        {lesson.number} - {lesson.title}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
              )}

              {notification.destination.screen === 'MainTabNavigator' && (
                <Grid item xs={12} md={4}>
                  <Select
                    label="Aba *"
                    id="tab"
                    name="tab"
                    fullWidth
                    handleChange={handleDestinationChange}
                    value={notification.destination.tab || 'Dashboard'}
                  >
                    {MAIN_TABS.map((t) => (
                      <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                    ))}
                  </Select>
                </Grid>
              )}
            </Grid>

            <Divider style={{ margin: '16px 0' }} />

            <Text variant="h6">Audiência</Text>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <Select
                  label="Tipo de audiência"
                  id="audienceType"
                  name="audienceType"
                  fullWidth
                  handleChange={handleAudienceTypeChange}
                  value={notification.audience.type}
                >
                  {AUDIENCE_TYPES.map((a) => (
                    <MenuItem key={a.value} value={a.value}>{a.label}</MenuItem>
                  ))}
                </Select>
              </Grid>

              {notification.audience.type === 'user' && (
                <Grid item xs={12} md={8}>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => setState((prev) => ({
                      ...prev,
                      showUserSelect: true,
                      userSelectMulti: false
                    }))}
                  >
                    {selectedUser ? `Usuário: ${selectedUser.name}` : 'Selecionar usuário'}
                  </Button>
                  {state.errors.audience && (
                    <Text style={{ color: 'red', marginTop: 8 }}>{state.errors.audience}</Text>
                  )}
                </Grid>
              )}

              {notification.audience.type === 'users' && (
                <Grid item xs={12} md={8}>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => setState((prev) => ({
                      ...prev,
                      showUserSelect: true,
                      userSelectMulti: true
                    }))}
                    style={{ marginBottom: 8 }}
                  >
                    Selecionar usuários ({selectedUsers.length})
                  </Button>
                  <div>
                    {selectedUsers.map((u) => (
                      <Chip
                        key={u.id || u._id}
                        label={u.name}
                        style={{ marginRight: 4, marginBottom: 4 }}
                      />
                    ))}
                  </div>
                  {state.errors.audience && (
                    <Text style={{ color: 'red', marginTop: 8 }}>{state.errors.audience}</Text>
                  )}
                </Grid>
              )}

              {notification.audience.type === 'group' && (
                <Grid item xs={12} md={8}>
                  <Select
                    label="Grupo salvo *"
                    id="groupId"
                    name="groupId"
                    fullWidth
                    handleChange={handleGroupChange}
                    value={notification.audience.groupId || ''}
                  >
                    <MenuItem value="">Selecione um grupo...</MenuItem>
                    {userGroups.map((group) => (
                      <MenuItem key={group.id} value={group.id}>
                        {group.name} ({group.users?.length || 0} membros)
                      </MenuItem>
                    ))}
                  </Select>
                  {state.errors.audience && (
                    <Text style={{ color: 'red', marginTop: 8 }}>{state.errors.audience}</Text>
                  )}
                </Grid>
              )}

              {notification.audience.type === 'segment' && (
                <Grid item xs={12}>
                  <Text style={{ marginBottom: 8, color: '#666' }}>
                    Segmento dinâmico — critérios combinados resolvem a audiência no momento do envio
                  </Text>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={3}>
                      <Select
                        label="Idioma"
                        id="language"
                        name="language"
                        fullWidth
                        handleChange={handleSegmentChange}
                        value={notification.audience.segment?.language || ''}
                      >
                        {LANGUAGE_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                      </Select>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        label="Dias inativos (mín.)"
                        variant="outlined"
                        name="inactiveDays"
                        type="number"
                        fullWidth
                        value={notification.audience.segment?.inactiveDays ?? ''}
                        onChange={handleSegmentChange}
                        inputProps={{ min: 0 }}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        label="País"
                        variant="outlined"
                        name="country"
                        fullWidth
                        value={notification.audience.segment?.country || ''}
                        onChange={handleSegmentChange}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        label="Estado"
                        variant="outlined"
                        name="state"
                        fullWidth
                        value={notification.audience.segment?.state || ''}
                        onChange={handleSegmentChange}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        label="Cidade"
                        variant="outlined"
                        name="city"
                        fullWidth
                        value={notification.audience.segment?.city || ''}
                        onChange={handleSegmentChange}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            name="speaker"
                            checked={!!notification.audience.segment?.speaker}
                            onChange={handleSegmentChange}
                            color="primary"
                          />
                        }
                        label="Speaker"
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            name="hasCompletedLessons"
                            checked={!!notification.audience.segment?.hasCompletedLessons}
                            onChange={handleSegmentChange}
                            color="primary"
                          />
                        }
                        label="Com lições concluídas"
                      />
                    </Grid>
                  </Grid>
                </Grid>
              )}
            </Grid>

            <Divider style={{ margin: '16px 0' }} />

            <Text variant="h6">Agendamento</Text>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <DataPicker
                  label="Data"
                  variant="outlined"
                  fullWidth
                  value={scheduledDate}
                  onChange={(e) => {
                    setScheduledDate(e.target.value);
                    setPreviewCount(null);
                  }}
                  error={state.errors.hasOwnProperty('scheduledDate')}
                  helperText={state.errors.scheduledDate}
                  style={{ marginBottom: 16 }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TimePicker
                  label="Horário"
                  variant="outlined"
                  fullWidth
                  value={scheduledTime}
                  onChange={(e) => {
                    setScheduledTime(e.target.value);
                    setPreviewCount(null);
                  }}
                  error={state.errors.hasOwnProperty('scheduledTime')}
                  helperText={state.errors.scheduledTime || state.errors.scheduledAt}
                  style={{ marginBottom: 16 }}
                />
              </Grid>
            </Grid>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 24 }}>
              <Button
                style={{ marginRight: 10 }}
                variant="outlined"
                color="primary"
                onClick={handleCancel}
                disabled={!!sendProgress}
              >
                Cancelar
              </Button>
              <Button
                style={{ marginRight: 10 }}
                variant="outlined"
                color="primary"
                onClick={handleSaveDraft}
                disabled={!!sendProgress}
              >
                Salvar rascunho
              </Button>
              <Button
                style={{ marginRight: 10 }}
                variant="outlined"
                color="primary"
                onClick={handleScheduleClick}
                disabled={!!sendProgress}
              >
                Agendar
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSendNowClick}
                disabled={!!sendProgress}
              >
                Enviar agora
              </Button>
            </div>
          </Paper>
        </Grid>
      </Grid>

      {state.showConfirmDialog && (
        <ConfirmDialog
          open={state.showConfirmDialog}
          title="Confirmar envio"
          question={getConfirmQuestion()}
          handleConfirm={handleConfirmSend}
          handleClose={() => setState((prev) => ({ ...prev, showConfirmDialog: false }))}
          confimButtonLabel="Enviar"
          cancelButtonLabel="Cancelar"
        />
      )}

      {state.showScheduleConfirmDialog && (
        <ConfirmDialog
          open={state.showScheduleConfirmDialog}
          title="Confirmar agendamento"
          question={getScheduleConfirmQuestion()}
          handleConfirm={handleConfirmSchedule}
          handleClose={() => setState((prev) => ({ ...prev, showScheduleConfirmDialog: false }))}
          confimButtonLabel="Agendar"
          cancelButtonLabel="Cancelar"
        />
      )}

      {state.showUserSelect && (
        <UserSelect
          open={state.showUserSelect}
          multiSelect={state.userSelectMulti}
          handleClose={() => setState((prev) => ({ ...prev, showUserSelect: false }))}
          handleSelected={handleUserSelected}
          labelConfirmButton="Confirmar"
        />
      )}
    </>
  );
}
